/**
 * Data states of TZ 8.12.4 and 6a.10: loading (skeleton), empty ("bu davrda ma'lumot yo'q"), error with retry,
 * partial data (yellow mark with outage intervals in the tooltip), and "server offline, data from 14:02".
 */
import { AlertTriangleIcon, CloudOffIcon, InboxIcon, RotateCwIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useUiTranslation } from '../i18n/ui_i18n';
import { Button } from '../primitives/button';
import { Tooltip } from '../primitives/overlay_primitives';
import { Skeleton } from '../primitives/skeleton';
import { classNames } from '../styling/class_names';

export type SkeletonShape = 'chart' | 'kpi' | 'table' | 'list' | 'tiles';

export interface LoadingSkeletonProps {
  shape?: SkeletonShape;
  rowCount?: number;
  className?: string;
}

export function LoadingSkeleton({ shape = 'chart', rowCount = 6, className }: LoadingSkeletonProps) {
  const { t: translate } = useUiTranslation();
  const rowIndexes = Array.from({ length: rowCount }, (_, rowIndex) => rowIndex);
  return (
    <div role="status" aria-live="polite" className={classNames('flex flex-col gap-2', className)}>
      <span className="sr-only">{translate('state.loading')}</span>
      {shape === 'chart' ? <Skeleton className="h-56 w-full" /> : null}
      {shape === 'kpi' ? (
        <>
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-3 w-20" />
        </>
      ) : null}
      {shape === 'table' || shape === 'list'
        ? rowIndexes.map((rowIndex) => (
            <Skeleton key={rowIndex} className={shape === 'table' ? 'h-8 w-full' : 'h-10 w-full'} />
          ))
        : null}
      {shape === 'tiles' ? (
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {rowIndexes.map((rowIndex) => (
            <Skeleton key={rowIndex} className="h-20" />
          ))}
        </div>
      ) : null}
    </div>
  );
}

interface StateMessageProps {
  icon: ReactNode;
  title: string;
  description?: string | undefined;
  action?: ReactNode;
  className?: string | undefined;
  role?: 'status' | 'alert';
}

function StateMessage({ icon, title, description, action, className, role = 'status' }: StateMessageProps) {
  return (
    <div
      role={role}
      className={classNames(
        'flex min-h-40 flex-col items-center justify-center gap-2 px-4 py-8 text-center',
        className,
      )}
    >
      <div className="mb-1 grid size-11 place-items-center rounded-full bg-neutral-soft text-muted [&_svg]:size-5">
        {icon}
      </div>
      <p className="font-medium text-ink">{title}</p>
      {description === undefined ? null : (
        <p className="max-w-xl text-sm text-muted" data-helper-text="">
          {description}
        </p>
      )}
      {action === undefined ? null : <div className="mt-2">{action}</div>}
    </div>
  );
}

export interface EmptyStateProps {
  title?: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ title, description, action, className }: EmptyStateProps) {
  const { t: translate } = useUiTranslation();
  return (
    <StateMessage
      icon={<InboxIcon aria-hidden />}
      title={title ?? translate('state.empty_title')}
      description={description}
      action={action}
      className={className}
    />
  );
}

export interface ErrorStateProps {
  title?: string;
  description?: string;
  onRetry?: () => void;
  className?: string;
}

export function ErrorState({ title, description, onRetry, className }: ErrorStateProps) {
  const { t: translate } = useUiTranslation();
  return (
    <StateMessage
      role="alert"
      icon={<AlertTriangleIcon aria-hidden className="text-bad" />}
      title={title ?? translate('state.error_title')}
      description={description ?? translate('state.error_description')}
      className={className}
      action={
        onRetry === undefined ? undefined : (
          <Button size="sm" onClick={onRetry}>
            <RotateCwIcon aria-hidden />
            {translate('state.retry')}
          </Button>
        )
      }
    />
  );
}

export interface StaleDataNoticeProps {
  /** Preformatted time of the last successful update, e.g. "14:02". */
  lastUpdatedTime: string;
  className?: string;
}

/** 6a.10: "server oflayn — ma'lumot oxirgi marta 14:02 da olingan". */
export function StaleDataNotice({ lastUpdatedTime, className }: StaleDataNoticeProps) {
  const { t: translate } = useUiTranslation();
  return (
    <div
      role="status"
      className={classNames(
        'flex items-center gap-2 rounded-control bg-warn-soft px-3 py-2 text-sm text-warn',
        className,
      )}
    >
      <CloudOffIcon className="size-4" aria-hidden />
      {translate('state.stale', { time: lastUpdatedTime })}
    </div>
  );
}

export interface OutageInterval {
  /** Preformatted "14:10–14:35" labels (formatting happens where the time zone is known). */
  label: string;
}

export interface PartialDataBadgeProps {
  /** Coverage 0..100 (9.1 "To'liqlik"). */
  coveragePercent: number;
  outages?: readonly OutageInterval[];
  className?: string;
}

/** 8.12.4 partial data: yellow mark; the tooltip lists outage intervals. Never hidden (8.8). */
export function PartialDataBadge({ coveragePercent, outages = [], className }: PartialDataBadgeProps) {
  const { t: translate } = useUiTranslation();
  const tooltipContent = (
    <div className="flex flex-col gap-1">
      <span>{translate('state.partial_coverage', { percent: Math.round(coveragePercent) })}</span>
      {outages.length > 0 ? (
        <>
          <span className="font-medium">{translate('state.outages')}</span>
          <ul>
            {outages.map((outage) => (
              <li key={outage.label}>{outage.label}</li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
  return (
    <Tooltip content={tooltipContent}>
      <button
        type="button"
        className={classNames(
          'inline-flex cursor-help items-center gap-1 rounded-full bg-warn-soft px-2 py-0.5 text-xs font-medium text-warn',
          className,
        )}
      >
        <AlertTriangleIcon className="size-3" aria-hidden />
        {translate('state.partial')}
      </button>
    </Tooltip>
  );
}

export interface QueryStateViewProps<ResultData> {
  isPending: boolean;
  isError: boolean;
  data: ResultData | undefined;
  onRetry?: () => void;
  isEmpty?: (resultData: ResultData) => boolean;
  skeleton?: SkeletonShape;
  emptyTitle?: string;
  emptyDescription?: string;
  children: (resultData: ResultData) => ReactNode;
}

/** Renders the right state for an async result; keeps every screen consistent (8.12.4, 6a.10). */
export function QueryStateView<ResultData>({
  isPending,
  isError,
  data,
  onRetry,
  isEmpty,
  skeleton = 'chart',
  emptyTitle,
  emptyDescription,
  children,
}: QueryStateViewProps<ResultData>) {
  if (isPending && data === undefined) {
    return <LoadingSkeleton shape={skeleton} />;
  }
  if (isError && data === undefined) {
    return <ErrorState onRetry={onRetry} />;
  }
  if (data === undefined) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }
  if (isEmpty?.(data) === true) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }
  return <>{children(data)}</>;
}
